import React, { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Settings, Clock, ArrowLeft, Users, Target, Lightbulb, Check, Gem, Star, X, AlertCircle } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface Activity {
  id: string;
  emoji: string;
  title: string;
  points: number;
  type: 'gold' | 'diamond';
  difficulty: 'Easy' | 'Medium' | 'Hard';
  time: string;
  description?: string;
  instructions?: string[];
  impact?: string;
  completionRate?: number;
}
interface ActivityDetailPageProps {
  activity: Activity;
  onNavigate: (page: string) => void;
  onBack: () => void;
}
export function ActivityDetailPage({
  activity,
  onNavigate,
  onBack
}: ActivityDetailPageProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  // Form data state
  const [kilometers, setKilometers] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [quantity, setQuantity] = useState('');
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setIsScrolled(scrollTop > 50);
  };
  const handleAddToActivities = () => {
    // Check if this action requires data input
    const requiresData = needsDataInput(activity.title);
    if (requiresData) {
      setShowDataModal(true);
    } else {
      // Add directly without data
      handleConfirmAdd();
    }
  };
  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowDataModal(false);
      setIsClosing(false);
      setKilometers('');
      setFrequency('daily');
      setQuantity('');
    }, 300);
  };
  const handleConfirmAdd = () => {
    // In real app, would save to backend with collected data
    handleCloseModal();
    onNavigate('actions');
  };
  // Determine if action needs data input based on title
  const needsDataInput = (title: string): boolean => {
    const lowerTitle = title.toLowerCase();
    return lowerTitle.includes('bici') || lowerTitle.includes('lavoro') || lowerTitle.includes('mezzi pubblici') || lowerTitle.includes('lampadine') || lowerTitle.includes('led') || lowerTitle.includes('auto') || lowerTitle.includes('trasport');
  };
  // Get appropriate form fields based on activity
  const getFormFields = () => {
    const lowerTitle = activity.title.toLowerCase();
    if (lowerTitle.includes('bici') || lowerTitle.includes('mezzi pubblici') || lowerTitle.includes('auto')) {
      return 'transport';
    } else if (lowerTitle.includes('lampadine') || lowerTitle.includes('led')) {
      return 'bulbs';
    }
    return 'generic';
  };
  const difficultyLabels = {
    Easy: 'Facile',
    Medium: 'Medio',
    Hard: 'Difficile'
  };
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-101">
      {/* Fixed Header */}
      <header className={`fixed top-0 left-0 right-0 px-6 py-4 flex justify-between items-center z-30 transition-all duration-300 ${isScrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'}`} data-id="element-102">
        <div className="flex items-center h-8" data-id="element-103">
          <button onClick={onBack} className={`flex items-center gap-1.5 transition-colors duration-300 ${isScrolled ? 'text-gray-900 hover:text-green-600' : 'text-white hover:text-green-100'}`} data-id="element-104">
            <ArrowLeft className="h-6 w-6" strokeWidth={1.5} data-id="element-105" />
            <span className="text-sm font-normal" data-id="element-106">Indietro</span>
          </button>
        </div>
        <div className="flex items-center gap-3" data-id="element-107">
          <div className={`flex items-center gap-1.5 transition-colors duration-300 ${isScrolled ? 'text-green-600' : 'text-white'}`} data-id="element-108">
            <Gem className="h-5 w-5" strokeWidth={2} data-id="element-109" />
            <span className="text-sm font-normal" data-id="element-110">150</span>
          </div>
          <div className={`flex items-center gap-1.5 transition-colors duration-300 ${isScrolled ? 'text-gray-600' : 'text-white'}`} data-id="element-111">
            <Star className="h-5 w-5" strokeWidth={2} data-id="element-112" />
            <span className="text-sm font-normal" data-id="element-113">400</span>
          </div>
        </div>
      </header>

      {/* Main Scrollable Content */}
      <ScrollArea className="flex-1" onScrollCapture={handleScroll} data-id="element-114">
        {/* Hero Section with Activity Info */}
        <div className="bg-green-600 w-full relative pt-16 pb-6" data-id="element-115">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
          backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/wsCbUWp4TF3ni7DwEfb9qP/hand-drawn-turtle-shell-pattern_23-2150598661.jpg)'
        }} data-id="element-116" />
          <div className="relative z-10 px-6 max-w-md mx-auto" data-id="element-117">
            <div className="text-7xl mb-4" data-id="element-118">{activity.emoji}</div>
            <h1 className="text-2xl font-normal text-white mb-4" data-id="element-119">
              {activity.title}
            </h1>

            {/* Stats Row */}
            <div className="flex items-center gap-3 flex-wrap" data-id="element-120">
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5" data-id="element-121">
                <Clock className="h-4 w-4 text-white" data-id="element-122" />
                <span className="text-sm font-normal text-white" data-id="element-123">
                  {activity.time}
                </span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5" data-id="element-124">
                {activity.type === 'diamond' ? <Gem className="h-4 w-4 text-white" data-id="element-125" /> : <Star className="h-4 w-4 text-white" fill="white" data-id="element-126" />}
                <span className="text-sm font-normal text-white" data-id="element-127">
                  +{activity.points}
                </span>
              </div>
              <div className={`text-xs font-normal px-3 py-1.5 rounded-full ${activity.difficulty === 'Easy' ? 'bg-green-100 text-green-700' : activity.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-128">
                {difficultyLabels[activity.difficulty]}
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="px-6 py-6 pb-28 max-w-md mx-auto space-y-6" data-id="element-129">
          {/* Completion Rate */}
          {activity.completionRate !== undefined && <div className="bg-white rounded-xl p-5 border border-gray-100" data-id="element-130">
              <div className="flex items-center justify-between mb-3" data-id="element-131">
                <div className="flex items-center gap-2" data-id="element-132">
                  <Users className="h-5 w-5 text-gray-600" data-id="element-133" />
                  <span className="text-sm font-normal text-gray-700" data-id="element-134">
                    Completamento community
                  </span>
                </div>
                <span className="text-xl font-normal text-green-600" data-id="element-135">
                  {activity.completionRate}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-136">
                <div className="bg-green-600 h-full rounded-full transition-all duration-500" style={{
              width: `${activity.completionRate}%`
            }} data-id="element-137" />
              </div>
              <p className="text-xs text-gray-500 mt-3" data-id="element-138">
                {activity.completionRate > 70 ? '🔥 Azione molto popolare nella community!' : activity.completionRate > 40 ? '✨ Molti utenti la stanno provando' : '💪 Sfida impegnante ma gratificante'}
              </p>
            </div>}

          {/* Description */}
          {activity.description && <div className="bg-white rounded-xl p-5 border border-gray-100" data-id="element-139">
              <h3 className="text-sm font-normal text-gray-900 mb-3 tracking-wide flex items-center gap-2" data-id="element-140">
                <Lightbulb className="h-4 w-4 text-green-600" data-id="element-141" />
                Descrizione
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-142">
                {activity.description}
              </p>
            </div>}

          {/* Impact */}
          {activity.impact && <div className="bg-white rounded-xl p-5 border border-gray-100" data-id="element-143">
              <h3 className="text-sm font-normal text-gray-900 mb-3 tracking-wide flex items-center gap-2" data-id="element-144">
                <Target className="h-4 w-4 text-green-600" data-id="element-145" />
                Impatto ambientale
              </h3>
              <div className="bg-green-50 border border-green-100 rounded-xl p-4" data-id="element-146">
                <p className="text-sm text-green-900 leading-relaxed" data-id="element-147">
                  {activity.impact}
                </p>
              </div>
            </div>}

          {/* Instructions */}
          {activity.instructions && activity.instructions.length > 0 && <div className="bg-white rounded-xl p-5 border border-gray-100" data-id="element-148">
              <h3 className="text-sm font-normal text-gray-900 mb-4 tracking-wide flex items-center gap-2" data-id="element-149">
                <Check className="h-4 w-4 text-green-600" data-id="element-150" />
                Come fare
              </h3>
              <div className="space-y-3" data-id="element-151">
                {activity.instructions.map((instruction, index) => <div key={index} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-152">
                    <div className="flex-shrink-0 w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-normal" data-id="element-153">
                      {index + 1}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed flex-1 pt-0.5" data-id="element-154">
                      {instruction}
                    </p>
                  </div>)}
              </div>
            </div>}

          {/* CTA Button */}
          <div className="pt-2" data-id="element-155">
            <button onClick={handleAddToActivities} className="w-full py-4 px-4 rounded-full text-base font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-156">
              Aggiungi alle attività
            </button>
          </div>
        </div>
      </ScrollArea>

      <BottomNav activePage="impact" onNavigate={onNavigate} data-id="element-157" />

      {/* Data Input Modal */}
      {showDataModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseModal} data-id="element-158" />

          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-159">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-160">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-161" />
              <div className="flex items-center justify-between" data-id="element-162">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-163">
                  Dettagli azione
                </h3>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-164">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-165" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-166">
              <div className="space-y-6" data-id="element-167">
                {/* Activity Preview */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-168">
                  <div className="flex items-center gap-3" data-id="element-169">
                    <div className="text-3xl" data-id="element-170">{activity.emoji}</div>
                    <div className="flex-1" data-id="element-171">
                      <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-172">
                        {activity.title}
                      </h4>
                      <div className="flex items-center gap-1 text-xs text-gray-600" data-id="element-173">
                        {activity.type === 'diamond' ? <Gem className="h-3.5 w-3.5 text-green-600" strokeWidth={2} data-id="element-174" /> : <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-175" />}
                        <span data-id="element-176">+{activity.points} punti</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dynamic Form Fields */}
                {getFormFields() === 'transport' && <>
                    <div data-id="element-177">
                      <label className="block text-sm font-normal text-gray-900 mb-3" data-id="element-178">
                        Distanza del viaggio (km)
                      </label>
                      <input type="number" value={kilometers} onChange={e => setKilometers(e.target.value)} placeholder="Es: 10" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-green-500 transition-colors" data-id="element-179" />
                    </div>

                    <div data-id="element-180">
                      <label className="block text-sm font-normal text-gray-900 mb-3" data-id="element-181">
                        Frequenza
                      </label>
                      <select value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-green-500 transition-colors" data-id="element-182">
                        <option value="daily" data-id="element-183">Giornaliera</option>
                        <option value="weekly" data-id="element-184">Settimanale</option>
                        <option value="monthly" data-id="element-185">Mensile</option>
                        <option value="occasional" data-id="element-186">Occasionale</option>
                      </select>
                    </div>
                  </>}

                {getFormFields() === 'bulbs' && <div data-id="element-187">
                    <label className="block text-sm font-normal text-gray-900 mb-3" data-id="element-188">
                      Quante lampadine vuoi sostituire?
                    </label>
                    <input type="number" value={quantity} onChange={e => setQuantity(e.target.value)} placeholder="Es: 5" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-green-500 transition-colors" data-id="element-189" />
                    <p className="text-xs text-gray-500 mt-2" data-id="element-190">
                      Ti aiuteremo a calcolare l'impatto basato sul numero di
                      lampadine
                    </p>
                  </div>}

                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-191">
                  <div className="flex items-start gap-3" data-id="element-192">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-193" />
                    <div data-id="element-194">
                      <p className="text-xs text-blue-900 leading-relaxed" data-id="element-195">
                        Questi dati ci aiutano a calcolare con precisione il tuo
                        impatto ambientale e a fornirti statistiche
                        personalizzate.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white space-y-3" data-id="element-196">
              <button onClick={handleConfirmAdd} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-197">
                Aggiungi alle attività
              </button>
              <button onClick={handleCloseModal} className="w-full py-3 px-4 rounded-full text-sm font-normal text-gray-600 bg-white border-2 border-gray-200 hover:bg-gray-50 transition-all" data-id="element-198">
                Annulla
              </button>
            </div>
          </div>
        </>}
    </div>;
}